<template>
  <div>
    <!-- Using DialogWrapper for both modal and tab modes -->
    <dialog-wrapper
      :tab-mode="tabMode"
      :dialog="dialog"
      :loading="loading"
      :title="'Novi račun'"
      :absolute="absolute"
      :opacity="opacity"
    >
      <v-container class="pb-0">
        <!-- Using ReceiptFormHeader component -->
        <receipt-form-header
          :receipt-number="receiptNumber"
          :location.sync="location"
          :locations="locations"
          :location-disabled="locationDisabled"
          :cash-register.sync="cashRegister"
          :cash-registers="cashRegisters"
          :payment-type.sync="paymentType"
          :payment-types="paymentTypes"
          @select-location="onSelectLocation"
          @select-register="onSelectRegister"
          @select-payment-type="onSelectPaymentType"
        />

        <cart-items-list
          ref="cartItemsList"
          :key="`cart-${shouldUseCompactLayout}`"
          :use-compact-layout="shouldUseCompactLayout"
          :cart="cart"
          :search-keywords="searchKeywords"
          :selected-items="selectedItems"
          :items="items"
          :is-loading="isLoading"
          :comboboxes="comboboxes"
          :cash-register="cashRegister"
          :taxes="taxes"
          :tax-label="$t('$vuetify.taxName')"
          :currency-symbol="currencySymbol"
          :eracun="eracun"
          :tax-categories="taxCategories"
          :tax-exemption-reasons="taxExemptionReasons"
          :item-types="itemTypes"
          :can-add-new-item="canAddNewItemComputed"
          :validation-errors="validationErrorsComputed"
          :formatted-total="showTotal()"
          :selected-currency.sync="selectedCurrency"
          :currencies="currencies"
          @update-search-keyword="
            (index, value) => {
              searchKeywords[index] = value;
              onSearchInput(index, value);
            }
          "
          @update-selected-item="handleSelectedItemUpdate"
          @item-change="handleItemChange"
          @remove-item="removeCartItem"
          @update-unit="
            (index, value) => {
              $set(cart[index], 'unit', value);
              updateCartItem(index);
            }
          "
          @update-tax="
            (index, value) => {
              $set(cart[index], 'tax', value);
              updateCartItem(index);
            }
          "
          @update-quantity-input="
            (index, value) => {
              cart[index].formattedQuantity = value;
            }
          "
          @update-quantity-blur="
            (index) => {
              $nextTick(() => updateQuantity(index));
            }
          "
          @update-price-input="
            (index, value) => {
              cart[index].formattedPrice = value;
            }
          "
          @update-price-blur="
            (index) => {
              $nextTick(() => updatePrice(index));
            }
          "
          @update-vpc-price-input="
            (index, value) => {
              cart[index].formattedVpcPrice = value;
            }
          "
          @update-vpc-price-blur="
            (index) => {
              updateVpcPrice(index);
            }
          "
          @update-discount-input="
            (index, value) => {
              cart[index].formattedDiscount = value;
            }
          "
          @update-discount-blur="
            (index) => {
              $nextTick(() => updateDiscount(index));
            }
          "
          @update-kpdcode="
            (index, value) => {
              cart[index].kpdcode = value;
              onKpdcodeChange(index);
            }
          "
          @update-tax-category="
            (index, value) => {
              cart[index].taxCategory = value;
              onTaxInfoChange();
            }
          "
          @update-tax-exemption-reason="
            (index, value) => {
              cart[index].taxExemptionReason = value;
              onTaxInfoChange();
            }
          "
          @update-item-type="
            (index, value) => {
              cart[index].data.type = value;
            }
          "
          @add-item="addEmptyCartItem"
          @apply-mass-discount="applyMassDiscount"
          @open-search-modal="$emit('open-search-modal')"
        />
        <date-fields-section
          :payment-type="paymentType"
          :invoice-date.sync="invoiceDate"
          :value-date.sync="valueDate"
          :delivery-date.sync="deliveryDate"
          :invoice-date-label="invoiceDateLabel"
          :value-date-label="valueDateLabel"
          :delivery-date-label="deliveryDateLabel"
          :date-pickers-disabled="datePickersDisabled"
          :selected-payment-due.sync="selectedPaymentDue"
          :payment-dues="paymentDues"
          :selected-language.sync="selectedLanguage"
          :languages="languages"
          :tab-mode="tabMode"
          @update-value-date="updateValueDate"
        />
        <associate-details-panel
          :associates-panel.sync="associatesPanel"
          :associates="associates"
          :associate-by-name.sync="associateByName"
          :associate-address.sync="associateAddress"
          :associates-with-oib="associatesWithOib"
          :associate-by-oib.sync="associateByOib"
          :associate-zip.sync="associateZip"
          :associate-city.sync="associateCity"
          :countries="countries"
          :associate-country.sync="associateCountry"
          :country-label="$t('$vuetify.formDetails.country')"
          :cash-register="cashRegister"
          :cost-centers="costCenters"
          :selected-cost-center.sync="selectedCostCenter"
          :save-associate-enabled="saveAssociateEnabled"
          :save-associate-label="$t('$vuetify.formDetails.saveAssociate')"
          :do-save-account.sync="doSaveAccount"
          :in-eu="inEu"
          :is-transactional-receipt="
            paymentType &&
            (paymentType.value === 'T' || paymentType.value === 'P')
          "
          :vieses="vieses"
          :selected-vies.sync="selectedVies"
          :customer-types="customerTypes"
          :selected-customer-type.sync="selectedCustomerType"
          :payment-type-value="
            paymentType && paymentType.value ? paymentType.value : 'T'
          "
          :eracun.sync="eracun"
          :e-racun-enabled="eRacunEnabled"
          :loading-oib="loadingOib"
          :loading-name="loadingName"
          @update:oibSearchInput="onOibSearch"
          @update:nameSearchInput="onNameSearch"
          :e-racun-conditions="eRacunConditions"
          :eracun-email.sync="eracunEmail"
          :can-submit-receipt="canSubmitReceipt"
          :required-rule="rules.req"
          :email-rule="rules.email"
          :eracun-attachments.sync="eracunAttachments"
          :company-id="currentCompanyId"
          @select-associate="onSelectAssociate"
          @select-associate-oib="onSelectAssociateOib"
          @clear-associate-fields="clearAssociateFields"
          @open-cost-center-dialog="openCostCenterDialog"
        />
        <fiscalization-panel
          :payment-type="paymentType"
          :fiscalize.sync="fiscalize"
          :fiscalization-options="fiscalizationOptions"
          :has-certificate="hasCertificate"
          :fiscalization-conditions="fiscalizationConditions"
          :fiscalization-enabled="fiscalizationEnabled"
          @fiscalization-change="onFiscalizationChange"
        />
        <v-row
          v-if="showPnb() || (paymentType && paymentType.value === 'T')"
          dense
          style="margin-bottom: 10px"
        >
          <v-col cols="12">
            <v-expansion-panels style="margin-bottom: 10px">
              <v-expansion-panel>
                <v-expansion-panel-header disable-icon-rotate>
                  <div class="d-flex align-center">
                    <v-icon class="mr-2">mdi-pound-box-outline</v-icon>
                    <span>
                      Poziv na broj
                      <span v-if="!customPnb" class="caption grey--text ml-1">· automatski prema broju računa</span>
                      <span v-else-if="manualPaymentReference" class="caption primary--text ml-1">· {{ manualPaymentReference }}</span>
                    </span>
                    <v-spacer></v-spacer>
                    <v-chip
                      small
                      :color="customPnb ? 'primary' : 'success'"
                      text-color="white"
                    >
                      {{ customPnb ? 'Ručno' : 'Automatski' }}
                    </v-chip>
                  </div>
                  <template v-slot:actions>
                    <v-icon>mdi-chevron-down</v-icon>
                  </template>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row dense style="margin-top: -0.5rem">
                    <v-col cols="12">
                      <div class="caption grey--text mb-3">
                        Ako ne upišete poziv na broj, automatski će biti generiran prema broju računa (npr. HR00 48-1-1).
                      </div>
                      <v-switch
                        v-model="customPnb"
                        label="Upiši poziv na broj ručno"
                        color="primary"
                        inset
                        hide-details
                        class="mt-0 mb-3"
                        @change="manualPaymentReference = ''"
                      ></v-switch>
                      <v-text-field
                        v-if="customPnb"
                        v-model="manualPaymentReference"
                        label="Poziv na broj"
                        placeholder="npr. HR00 12-34-56"
                        outlined
                        dense
                        clearable
                        hide-details="auto"
                        class="mt-1"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>
        </v-row>

        <comment-panel :comment-panel.sync="commentPanel" :note.sync="note" />
      </v-container>

      <!-- Action buttons slot -->
      <template #actions>
        <v-row dense style="margin-top: 1rem; margin-bottom: 1rem">
          <!-- ZA PREDUJAM SAKRIVENO -->
          <v-menu
            v-if="false"
            bottom
            left
            flat
            rounded="false"
            nudge-bottom="60px"
            min-width="200px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                id="avans"
                class="btn warningButton"
                width="35%"
                v-bind="attrs"
                v-on="on"
                :disabled="!cashRegister"
              >
                <b>Račun za predujam</b>
              </v-btn>
            </template>
            <v-list style="min-width: 100%">
              <v-list-item :disabled="!cashRegister" @click="openAvans">
                <v-list-item-title> Račun za predujam </v-list-item-title>
              </v-list-item>
              <v-list-item :disabled="!cashRegister" @click="openAvansSummary">
                <v-list-item-title>
                  Poveži račun za predujam
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-spacer></v-spacer>
          <v-btn
            class="btn infoButton"
            style="margin-right: 1rem"
            @click="resetAll"
          >
            Počisti
          </v-btn>
          <v-btn
            class="btn dangerButton"
            style="margin-right: 1rem"
            @click="close"
          >
            Odustani
          </v-btn>
          <v-btn
            id="success"
            class="btn successButton"
            :disabled="!cashRegister || !canSubmitReceipt"
            @click="validateForm"
            ref="footer"
          >
            Završi
          </v-btn>
        </v-row>
      </template>
    </dialog-wrapper>

    <!-- Child dialogs - outside wrapper -->
    <show-error ref="showError"></show-error>
    <avans-dialog :associates="associates" ref="avans"></avans-dialog>
    <avans-summary :associates="associates" ref="avansSummary"></avans-summary>
    <cost-center-dialog
      @addCostCenter="addCostCenter"
      ref="costCenterDialog"
    ></cost-center-dialog>

    <!-- Snackbar za notifikacije -->
    <v-snackbar
      v-model="snackbar"
      :timeout="3000"
      :color="snackbarColor"
      top
      right
    >
      <v-icon left color="white">mdi-information</v-icon>
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false" color="white">
          Zatvori
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>
<script>
import { auth, df } from '@/plugins/firebase'
import state from '@/state'
import EventBus from '@/plugins/event-bus'
import templatePayment from '@/mixins/templatePayment.js'
import { clone } from '@/plugins/utils'
import { v4 as uuidv4 } from 'uuid'
import { debounce } from 'lodash'
import countries from '@/mixins/countries'
import ShowError from '@/components/ShowError'
import rules from '@/plugins/rules'
import AvansDialog from '@/modules/cash-register/components/dialogs/avans/AvansDialog.vue'
import AvansSummary from '@/modules/cash-register/components/dialogs/avans/AvansSummary.vue'
import AvansDeducted from '@/model/AvansDeducted'
// DatePicker now used in DateFieldsSection component
// import DatePicker from 'vue2-datepicker'
// import 'vue2-datepicker/locale/hr.js'
import triGram from '@/mixins/trigram'
import { collection, query, getDocs, where, limit } from 'firebase/firestore'
import { callAssociates, callBusinessEntities } from '@/api/associates'
import CostCenterDialog from '@/modules/company/components/CostCenterDialog.vue'
// New extracted components
import DialogWrapper from '@/modules/cash-register/components/wrappers/DialogWrapper.vue'
import ReceiptFormHeader from '@/modules/cash-register/components/receipt-form/ReceiptFormHeader.vue'
import CartItemsList from '@/modules/cash-register/components/receipt-form/CartItemsList.vue'
import DateFieldsSection from '@/modules/cash-register/components/receipt-form/DateFieldsSection.vue'
import AssociateDetailsPanel from '@/modules/cash-register/components/receipt-form/AssociateDetailsPanel.vue'
import FiscalizationPanel from '@/modules/cash-register/components/receipt-form/FiscalizationPanel.vue'
import PnbSection from '@/modules/cash-register/components/receipt-form/PnbSection.vue'
import CommentPanel from '@/modules/cash-register/components/receipt-form/CommentPanel.vue'
// import ActionButtons from '@/modules/cash-register/components/receipt-form/ActionButtons.vue'

export default {
  components: {
    ShowError,
    AvansDialog,
    AvansSummary,
    CostCenterDialog,
    DialogWrapper,
    ReceiptFormHeader,
    CartItemsList,
    DateFieldsSection,
    AssociateDetailsPanel,
    FiscalizationPanel,
    PnbSection,
    CommentPanel
    // ActionButtons
  },
  mixins: [templatePayment, countries, triGram],
  inject: ['showMsgBox', 'confirm', 'confirmClose', 'showLoader', 'hideLoader', 'openPdfPrint', 'showMsgBoxHtml'],
  props: {
    tabMode: {
      type: Boolean,
      default: false
    },
    useCompactCartItems: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      dialog: false,
      isLoading: false,
      config: null,
      multiplier: null,
      selectedPrinter: null,
      selectedItems: [{ data: { name: '', id: '', searchName: '' } }],
      cartItem: { units: [] },
      currency: 'EUR',
      selectedCurrency: 'EUR', // samo za odabir simbola
      total: 0,
      fullTotal: 0,
      formatedTotal: '0,00',
      date: (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10),
      // invoiceDate: (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10),
      invoiceDate: Date.now(),
      issueDateTime: (Math.floor(new Date().getTime() / 1000)),
      valueDate: (() => {
        const futureDate = new Date(Date.now())
        futureDate.setDate(futureDate.getDate() + 8)
        while (futureDate.getDay() === 0 || futureDate.getDay() === 6) {
          futureDate.setDate(futureDate.getDate() + 1)
        }
        return futureDate.getTime()
      })(),
      deliveryDate: Date.now(),
      dateFormatted: this.formatDate((new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10)),
      locations: [],
      location: null,
      locationDisabled: false,
      cashRegisters: [],
      cashRegister: null,
      user: null,
      userIsSalesman: false,
      cashRegisterNumber: 0,
      cashRegisterType: '',
      listeners: [],
      items: [],
      cart: [],
      cartVersion: 0, // Counter za forsiranje reaktivnosti computed properties
      isCreatingReceipt: false, // Flag za praćenje da li ovaj dialog kreira račun
      takenOibs: [],
      associates: [],
      defaultAssociates: [],
      oibSearchInput: '',
      oibOptions: [],
      loadingOib: false,
      loadingName: false,
      oibSearchTimer: null,
      nameSearchTimer: null,
      searchInputTimer: null,
      isSelectingOib: false,
      isSelectingName: false,
      lastOibSearchHadResults: true,
      lastNameSearchHadResults: true,
      lastOibSearchQuery: '',
      lastNameSearchQuery: '',
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'warning',
      invoiceDateOpen: false,
      valueDateOpen: false,
      deliveryDateOpen: false,
      invoiceDateLabel: 'Datum računa',
      valueDateLabel: 'Datum valute',
      deliveryDateLabel: 'Datum isporuke',
      paymentType: {},
      paymentTypes: [],
      itemTypes: [
        { name: 'Usluga', value: 'SERVICE' },
        { name: 'Materijalno dobro', value: 'GOODS' }
      ],
      taxes: [
        { name: 'PDV 25%', value: 1.25, rate: 0.25 },
        { name: 'PDV 13%', value: 1.13, rate: 0.13 },
        { name: 'PDV 5%', value: 1.05, rate: 0.05 },
        { name: 'PDV 0%', value: 1, rate: 0 }
      ],
      languages: [
        { name: 'Hrvatski', value: 'hr' },
        { name: 'English', value: 'en' }
      ],
      currencies: ['EUR', 'USD', 'GBP'],
      euCountries: [],
      inEu: false,
      selectedVies: null,
      associatesPanel: [],
      associate: undefined,
      associateByName: undefined,
      associateByOib: undefined,
      associateAddress: undefined,
      associateZip: undefined,
      associateCity: undefined,
      associateCountry: undefined,
      pnb: undefined,
      pnbModel: undefined,
      pnbType: 0,
      pnbTypes: [{ text: 'Generiraj poziv po broju računa', value: 0, disabled: false }, { text: 'Generiraj poziv po OIB-u', value: 1, disabled: true }],
      customPnb: false,
      manualPaymentReference: '',
      selectedLanguage: { name: 'Hrvatski', value: 'hr' },
      loading: false,
      absolute: true,
      opacity: 1,
      commentPanel: [],
      note: '',
      selectedCustomerType: null,
      wholeSaleRcptType: '',
      rules: {
        req: rules.req(),
        oib: rules.oib(),
        email: v => !v || /.+@.+\..+/.test(v) || 'Email adresa nije valjana'
      },
      fiscalizationOptions: [
        { text: 'Fiskaliziraj', value: true },
        { text: 'Nemoj fiskalizirati', value: false }
      ],
      avansDeducted: new AvansDeducted(),
      cartItems: [],
      formattedTotalMp: '0,00',
      formattedTotalVp: '0,00',
      registerListener: undefined,
      registerSnapshotListener: null,
      searchKeywords: [],
      searching: false,
      searchKey: '',
      searchLoading: false,
      comboboxes: [{ disabled: false }],
      costCenters: [],
      selectedCostCenter: undefined,
      doSaveAccount: false,
      saveAssociateEnabled: true,
      selectedPaymentDue: 8,
      paymentDues: [
        1, 2, 3, 5, 6, 7, 8, 9, 10, 12, 14, 15, 30, 60, 120
      ],
      internalFiscalize: true,
      eracun: false,
      eracunEmail: '',
      eracunAttachments: [],
      eracunManuallyDisabled: false, // Flag to track if user manually disabled eRacun
      kpdUpdateCounter: 0, // Helper to trigger reactivity for KPD changes
      taxCategories: [
        { name: 'Nulta stopa PDV-a', value: 'Z' },
        { name: 'Isporuka unutar EU', value: 'O' },
        { name: 'Izvoz', value: 'G' },
        { name: 'Prijenos porezne obveze', value: 'AE' },
        { name: 'Oslobođeno PDV-a', value: 'E' },
        { name: 'Povratna naknada', value: 'K' },
        { name: 'Posebni porez na potrošnju u ugostiteljstvu', value: 'PP' },
        { name: 'Posebni porez na motorna vozila', value: 'PPMV' },
        { name: 'Nije predmet oporezivanja PDV-om', value: 'N' },
        { name: 'Ne podliježe PDV-u', value: 'ND' }
      ],
      taxExemptionReasons: [
        { name: 'Oslobođeno od PDV-a po članku 39. zakona o PDV-u', value: 'ART_39' },
        { name: 'Oslobođeno od PDV-a po članku 40. zakona o PDV-u', value: 'ART_40' },
        { name: 'Oslobođeno od PDV-a po članku 41. zakona o PDV-u', value: 'ART_41' },
        { name: 'Oslobođeno od PDV-a po članku 42. zakona o PDV-u', value: 'ART_42' },
        { name: 'Oslobođeno od PDV-a po članku 43. zakona o PDV-u', value: 'ART_43' },
        { name: 'Oslobođeno od PDV-a po članku 44. zakona o PDV-u', value: 'ART_44' },
        { name: 'Oslobođeno od PDV-a po članku 45. zakona o PDV-u', value: 'ART_45' },
        { name: 'Oslobođeno od PDV-a po članku 46. zakona o PDV-u', value: 'ART_46' },
        { name: 'Oslobođeno od PDV-a po članku 47. zakona o PDV-u', value: 'ART_47' },
        { name: 'Oslobođeno od PDV-a po članku 48. zakona o PDV-u', value: 'ART_48' },
        { name: 'Oslobođeno od PDV-a po članku 49. zakona o PDV-u', value: 'ART_49' },
        { name: 'Oslobođeno od PDV-a po članku 50. zakona o PDV-u', value: 'ART_50' },
        { name: 'Oslobođeno od PDV-a po članku 51. zakona o PDV-u', value: 'ART_51' },
        { name: 'Oslobođeno od PDV-a po članku 52. zakona o PDV-u', value: 'ART_52' },
        { name: 'Oslobođeno od PDV-a po članku 53. zakona o PDV-u', value: 'ART_53' },
        { name: 'Oslobođeno od PDV-a po članku 54. zakona o PDV-u', value: 'ART_54' },
        { name: 'Oslobođeno od PDV-a po članku 90. zakona o PDV-u', value: 'ART_90' }
      ],
      sourceMetadata: null // Metadata za virman tracking
    }
  },

  computed: {
    associatesWithOib () {
      // If there are OIB search results, use those
      if (this.oibOptions && this.oibOptions.length > 0) {
        return this.oibOptions
      }

      if (!this.associates) {
        return [] // Return empty array if associates haven't loaded yet
      }
      // Filter the associates array to include only those with at least one OIB
      return this.associates.filter(associate =>
        associate.oibs && Array.isArray(associate.oibs) && associate.oibs.length > 0
      )
    },
    receiptNumber () {
      if (!this.location) { return 'Broj računa' }
      if (!this.cashRegister) { return 'Broj računa' }
      return this.cashRegisterNumber +
        state.getCurrentCompany().receipt_numeration_separator +
        this.location.location_id +
        state.getCurrentCompany().receipt_numeration_separator +
        this.cashRegister.number
    },

    currencySymbol () {
      switch (this.selectedCurrency) {
        case 'USD':
          return '$'
        case 'GBP':
          return '£'
        case 'EUR':
        default: {
          return '€'
        }
      }
    },

    computedInvoiceDateFormatted () {
      return this.formatDate(this.invoiceDate)
    },

    computedValueDateFormatted () {
      return this.formatDate(this.valueDate)
    },

    computedDeliveryDateFormatted () {
      return this.formatDate(this.deliveryDate)
    },

    today () {
      const d = new Date()
      return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`
    },

    datePickersDisabled () {
      return this.paymentType.value === 'G' || this.paymentType.value === 'K' || this.paymentType.value === 'O' || this.fiscalize
      //  this.cashRegisterType === 'MP'
    },

    vieses () {
      return [
        { name: 'Kupac unutar VIES baze', value: 'inside' },
        { name: 'Kupac izvan VIES baze', value: 'outside' }
      ]
    },

    customerTypes () {
      return [
        { name: 'Kupac - pravna osoba', value: 'legal' },
        { name: 'Kupac - fizička osoba', value: 'person' }
      ]
    },

    hasCertificate () {
      const company = state.getCurrentCompany()
      return company?.certificate !== undefined && company?.certificate !== null && company?.certificate !== ''
    },

    fiscalizationConditions () {
      const dateAfter2026 = new Date() >= new Date('2025-01-01')
      return {
        hasCertificate: this.hasCertificate,
        dateAfter2026
      }
    },

    fiscalizationEnabled () {
      const conditions = this.fiscalizationConditions
      return conditions.hasCertificate && conditions.dateAfter2026
    },

    eRacunConditions () {
      const company = state.getCurrentCompany()

      const fiscalizationDisabled = !this.fiscalize
      const customerDataComplete = (
        (this.associateByName || this.associateByOib) &&
        this.associateAddress &&
        this.associateCity &&
        this.associateCountry
      )
      const customerFromCroatia = this.associateCountry === 'Hrvatska'
      // Use kpdUpdateCounter to trigger reactivity when KPD codes change
      // eslint-disable-next-line no-unused-expressions
      this.kpdUpdateCounter
      const allItemsHaveKpd = this.cart.every(item =>
        !item.data || !item.data.name || (item.kpdcode && item.kpdcode.trim() !== '')
      )

      // Check that all 0% VAT items have tax category and exemption reason
      const allZeroVatItemsHaveTaxInfo = this.cart.every(item => {
        if (!item.data || !item.data.name || !item.tax) return true
        if (item.tax.rate !== 0) return true
        return item.taxCategory && item.taxExemptionReason
      })

      const providerActive = company?.eracun?.provider && company?.eracun?.active === true
      const dateAfter2026 = new Date() >= new Date('2025-01-01')

      return {
        fiscalizationDisabled,
        customerDataComplete,
        customerFromCroatia,
        allItemsHaveKpd,
        allZeroVatItemsHaveTaxInfo,
        providerActive,
        dateAfter2026
      }
    },

    currentCompanyId () {
      return state.getCurrentCompany().id || ''
    },

    eRacunEnabled () {
      const conditions = this.eRacunConditions
      return conditions.fiscalizationDisabled &&
             conditions.customerFromCroatia &&
             conditions.providerActive
    },

    canSubmitReceipt () {
      // Additional check for submitting receipt with eRacun
      if (this.eracun && this.paymentType.value === 'T') {
        const conditions = this.eRacunConditions
        return conditions.allItemsHaveKpd && conditions.allZeroVatItemsHaveTaxInfo
      }
      return true
    },

    eRacunConditionsExceptFiscalization () {
      const conditions = this.eRacunConditions
      return conditions.customerFromCroatia && conditions.providerActive
    },

    shouldUseCompactLayout () {
      // Ako je prop useCompactCartItems postavljen (iz CompactRegister template-a), koristi to
      if (this.useCompactCartItems) {
        return true
      }
      // Inače, provjeri template trenutne blagajne
      if (this.cashRegister && this.cashRegister.register_template) {
        return this.cashRegister.register_template === 'ai-compact'
      }
      return false
    },

    fiscalize: {
      get () {
        // If fiscalization is not enabled, always return false
        if (!this.fiscalizationEnabled) {
          return false
        }
        return this.internalFiscalize
      },
      set (value) {
        this.internalFiscalize = value
      }
    },

    canAddNewItemComputed () {
      // Koristi cartVersion da forsira re-compute
      this.cartVersion // eslint-disable-line no-unused-expressions
      let hasInvalidItem = false
      for (let i = 0; i < this.cart.length; i++) {
        const item = this.cart[i]
        const hasTax = item.tax && typeof item.tax === 'object' && item.tax.rate !== undefined
        const hasName = item.data?.name && item.data.name.trim() !== ''
        const hasUnit = item.unit !== null && item.unit !== undefined
        const hasPrice = item.price !== null && item.price !== undefined && item.price > 0

        // Za free item koji se još kuca, provjeravamo da li ima sve podatke
        if (item.data?.freeItem && hasName) {
          // Čak i ako selectItem nije pozvan, možemo provjeriti formattedPrice i osnovne podatke
          const formattedPriceValue = item.formattedPrice ? parseFloat(item.formattedPrice.toString().replace(',', '.')) : 0
          const hasFormattedPrice = formattedPriceValue > 0
          const hasValidPrice = hasPrice || hasFormattedPrice

          if (!hasTax || !hasValidPrice) {
            hasInvalidItem = true
            break
          }
        } else if (!hasName || !hasUnit || !hasTax || !hasPrice) {
          hasInvalidItem = true
          break
        }
      }
      return hasInvalidItem
    },

    validationErrorsComputed () {
      // Koristi cartVersion da forsira re-compute
      this.cartVersion // eslint-disable-line no-unused-expressions
      const errors = {}
      for (let i = 0; i < this.cart.length; i++) {
        const hasTax = this.cart[i].tax && typeof this.cart[i].tax === 'object' && this.cart[i].tax.rate !== undefined
        errors[i] = {
          name: !this.cart[i].data?.name,
          unit: !this.cart[i].unit,
          tax: !hasTax,
          price: !this.cart[i].price || this.cart[i].price <= 0
        }
      }
      return errors
    }

  },

  watch: {
    associateByOib (nv) {
      // Original validation logic
      if (nv === undefined) {
        this.doSaveAccount = false
        this.saveAssociateEnabled = true
        if (this.tabMode) {
          this.notifyStateChange()
        }
        return
      }

      // Allow saving for both new entries (string) and existing clients (object)
      this.saveAssociateEnabled = true

      if (this.tabMode) {
        this.notifyStateChange()
      }
    },

    paymentType (nv) {
      if (nv.value === 'G' || nv.value === 'K' || nv.value === 'O') {
        this.setDateToNow()
      } else {
        this.setDateDefault()
      }
    },

    invoiceDate (nv) {
      if (this.paymentType.value === 'P' || this.paymentType.value === 'T') {
        this.updateValueDate()
      }
    },
    internalFiscalize (nv) {
      // Kada je fiskalizacija uključena, datum računa mora biti današnji
      if (nv) {
        const savedValueDate = this.valueDate
        this.invoiceDate = Date.now()
        this.$nextTick(() => { this.valueDate = savedValueDate })
      }
    },
    associateCountry (nv) {
      this.inEu = false
      this.euCountries.forEach(country => {
        if (country === nv) {
          this.inEu = true
        }
      })

      // Automatically set fiscalization when country is not Croatia based on customer type
      if (nv && nv !== 'Hrvatska') {
        // If customer type is already selected, respect it
        if (this.selectedCustomerType === 'legal') {
          // Pravna osoba → fiscalization = No
          this.internalFiscalize = false
        } else if (this.selectedCustomerType === 'person') {
          // Fizička osoba → fiscalization = Yes
          this.internalFiscalize = true
        } else {
          // Default: fiscalization = No (when customer type not yet selected)
          this.internalFiscalize = false
        }
      }

      this.checkAndApplyEracunDefaults()
    },
    associateByName: {
      handler: debounce(function () {
        this.checkAndApplyEracunDefaults()
        if (this.tabMode) {
          this.notifyStateChange()
        }
      }, 500)
    },
    associateAddress () {
      this.checkAndApplyEracunDefaults()
    },
    associateCity () {
      this.checkAndApplyEracunDefaults()
    },
    selectedCustomerType (newVal) {
      // For non-Croatian customers:
      // "Kupac - pravna osoba" (legal) → fiscalization = No
      // "Kupac - fizička osoba" (person) → fiscalization = Yes
      if (this.associateCountry && this.associateCountry !== 'Hrvatska') {
        if (newVal === 'legal') {
          this.internalFiscalize = false
        } else if (newVal === 'person') {
          this.internalFiscalize = true
        }
      }
    },
    cart: {
      handler: debounce(function () {
        this.checkAndApplyEracunDefaults()
        if (this.tabMode) {
          this.notifyStateChange()
        }
      }, 500),
      deep: true
    },
    eracun (newVal, oldVal) {
      if (newVal) {
        // When eRacun is enabled, disable fiscalization (eRacun and fiscalization are mutually exclusive)
        this.internalFiscalize = false
        // Clear manual disable flag when user enables eRacun
        this.eracunManuallyDisabled = false
      } else {
        // When eRacun is disabled, DON'T automatically enable fiscalization
        // (that creates circular logic where switch becomes disabled when user tries to turn it off)
        // Set flag to indicate user manually disabled eRacun
        // (only if all conditions are met - meaning it wasn't auto-disabled due to conditions)
        if (this.eRacunConditionsExceptFiscalization) {
          this.eracunManuallyDisabled = true
        }
      }
    },
    // Watchers za spremanje stanja u local storage (sa debounce)
    associate: {
      handler: debounce(function () {
        if (this.tabMode) {
          this.notifyStateChange()
        }
      }, 500)
    },
    paymentType: {
      handler: debounce(function () {
        if (this.tabMode) {
          this.notifyStateChange()
        }
      }, 500)
    },
    currency: {
      handler: debounce(function () {
        if (this.tabMode) {
          this.notifyStateChange()
        }
      }, 500)
    },
    note: {
      handler: debounce(function () {
        if (this.tabMode) {
          this.notifyStateChange()
        }
      }, 500)
    },
    selectedLanguage: {
      handler: debounce(function () {
        if (this.tabMode) {
          this.notifyStateChange()
        }
      }, 500)
    },
    pnb: {
      handler: debounce(function () {
        if (this.tabMode) {
          this.notifyStateChange()
        }
      }, 500)
    }
  },

  methods: {
    // Dohvati trenutno stanje
    getState () {
      return {
        cart: this.cart,
        associate: this.associate,
        associateByName: this.associateByName,
        associateByOib: this.associateByOib,
        associateAddress: this.associateAddress,
        associateZip: this.associateZip,
        associateCity: this.associateCity,
        associateCountry: this.associateCountry,
        paymentType: this.paymentType,
        currency: this.currency,
        selectedCurrency: this.selectedCurrency,
        invoiceDate: this.invoiceDate,
        valueDate: this.valueDate,
        deliveryDate: this.deliveryDate,
        note: this.note,
        selectedLanguage: this.selectedLanguage,
        pnb: this.pnb,
        pnbModel: this.pnbModel,
        pnbType: this.pnbType,
        selectedCustomerType: this.selectedCustomerType,
        wholeSaleRcptType: this.wholeSaleRcptType,
        sourceMetadata: this.sourceMetadata,
        selectedItems: this.selectedItems,
        searchKeywords: this.searchKeywords,
        comboboxes: this.comboboxes
      }
    },

    // Postavi stanje
    setState (state) {
      if (!state) return

      this.cart = state.cart || []
      this.associate = state.associate
      this.associateByName = state.associateByName
      this.associateByOib = state.associateByOib
      this.associateAddress = state.associateAddress
      this.associateZip = state.associateZip
      this.associateCity = state.associateCity
      this.associateCountry = state.associateCountry

      // Pronađi paymentType iz paymentTypes arraya po value-u
      if (state.paymentType && state.paymentType.value) {
        const matchingPaymentType = this.paymentTypes.find(pt => pt.value === state.paymentType.value)
        this.paymentType = matchingPaymentType || state.paymentType
      } else {
        this.paymentType = state.paymentType || {}
      }

      this.currency = state.currency || 'EUR'
      this.selectedCurrency = state.selectedCurrency || 'EUR'
      this.invoiceDate = state.invoiceDate
      this.valueDate = state.valueDate
      this.deliveryDate = state.deliveryDate
      this.note = state.note || ''
      this.selectedLanguage = state.selectedLanguage || { name: 'Hrvatski', value: 'hr' }
      this.pnb = state.pnb
      this.pnbModel = state.pnbModel
      this.pnbType = state.pnbType
      this.selectedCustomerType = state.selectedCustomerType
      this.wholeSaleRcptType = state.wholeSaleRcptType
      this.sourceMetadata = state.sourceMetadata || null

      // Restauriraj selectedItems, searchKeywords i comboboxes
      if (state.selectedItems) {
        this.selectedItems = state.selectedItems
      } else {
        // Fallback: ako nisu spremljeni, generiraj ih iz cart-a
        this.selectedItems = this.cart.map(cartItem => {
          if (cartItem && cartItem.data) {
            return {
              data: cartItem.data
            }
          }
          return { data: { name: '', id: '', searchName: '' } }
        })
      }

      if (state.searchKeywords) {
        this.searchKeywords = state.searchKeywords
      } else {
        this.searchKeywords = this.cart.map(() => '')
      }

      if (state.comboboxes) {
        this.comboboxes = state.comboboxes
      } else {
        this.comboboxes = this.cart.map(() => ({ disabled: false }))
      }

      // Preračunaj ukupno nakon što je stanje restorirano
      this.$nextTick(() => {
        // Prvo ažuriraj svaki cart item da bi se izračunao discounted_price
        this.cart.forEach((item, index) => {
          if (item.discountAmt && item.discountAmt > 0) {
            // Pozovi calculateDiscount da bi se postavio data.discounted_price
            if (!item.data.discounted_price || item.data.discounted_price === 0) {
              this.cart[index].data.discounted_price = Number(this.calculateDiscount(item).toFixed(2))
            }
          }
        })
        // Zatim izračunaj totale
        this.calculateTotals()
      })
    },

    // Obavijesti parent o promjeni stanja
    notifyStateChange () {
      this.$emit('state-changed', this.getState())
    },

    addCostCenter (costCenter) {
      var cc = clone(costCenter)
      cc.address.zip_code = cc.address.zipCode
      this.costCenters.push(cc)
    },
    openCostCenterDialog () {
      this.$refs.costCenterDialog.open(false)
    },
    updateValueDate () {
      var days = this.selectedPaymentDue
      if (!days || isNaN(days)) {
        return
      }

      const date = new Date(this.invoiceDate)
      let daysAdded = 0

      while (daysAdded < Number(days)) {
        date.setDate(date.getDate() + 1)

        const dayOfWeek = date.getDay()

        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          daysAdded++
        }
      }

      this.valueDate = date.getTime()
    },
    onFiscalizationChange () {
      if (this.fiscalize) {
        this.eracun = false
        this.eracunEmail = ''
        // Reset manual disable flag when fiscalization is enabled
        // (eRacun was disabled by system, not by user)
        this.eracunManuallyDisabled = false
      }
    },
    onKpdcodeChange (counter) {
      // Increment counter to trigger computed property reactivity
      this.kpdUpdateCounter++
      // Update eRacun availability when any item's kpdcode changes
      this.checkEracunAvailability()
      // Check and apply eRacun defaults (auto-disable fiscalization if all conditions met)
      this.checkAndApplyEracunDefaults()
    },
    onTaxInfoChange () {
      // Increment counter to trigger computed property reactivity for tax info changes
      this.kpdUpdateCounter++
    },
    checkEracunAvailability () {
      // Check if any item is missing kpdcode
      const hasItemWithoutKpdcode = this.cart.some(item =>
        item.data && item.data.name && (!item.kpdcode || item.kpdcode.trim() === '')
      )

      // If any item is missing kpdcode and eRacun was selected, disable it
      if (hasItemWithoutKpdcode && this.eracun) {
        this.eracun = false
        this.eracunEmail = ''
        // Reset manual disable flag when eRacun is disabled by system due to missing KPD
        this.eracunManuallyDisabled = false
      }
    },
    checkAndApplyEracunDefaults () {
      // Only for transactional receipts
      if (this.paymentType?.value !== 'T') {
        return
      }

      // Check if all eRacun conditions except fiscalization are met
      if (this.eRacunConditionsExceptFiscalization) {
        // Automatically disable fiscalization and enable eRacun
        // BUT ONLY if user hasn't manually disabled eRacun
        if (!this.eracunManuallyDisabled && (this.fiscalize || !this.eracun)) {
          this.internalFiscalize = false
          this.eracun = true
        }
      }
    },
    onSearchInput (counter, searchValue) {
      if (!this.cashRegister || this.cashRegister.search_type === 'load') {
        return
      }
      this.items = []
      this.searchKey = searchValue
      if (this.seaching) {
        return
      }

      this.seaching = true
      this.searchInputTimer = setTimeout(() => {
        this.items = []
        this.querySearch(this.searchKey)
        this.seaching = false
      }, 1000)
    },
    async querySearch (keyword) {
      if (!keyword || keyword.length < 3) {
        return
      }
      var trigramObject = this.triGram(keyword)
      this.isLoading = true

      var results = await this.searchItems(trigramObject)
      if (!results || results.length === 0) {
        this.isLoading = false
        return
      }

      results.forEach((it) => {
        var item = {}
        item.data = it
        item = this.setupItem(item)

        if (item.data.active) {
          this.items.push(item)
        }
      })

      this.isLoading = false
    },
    async searchItems (triGramObject = undefined) {
      var searchCollection = 'location_pricelist'

      const searchConstraints = []
      if (triGramObject) {
        Object.keys(triGramObject).forEach(name =>
          searchConstraints.push(where(`meta.${name}`, '==', true))
        )
      }

      searchConstraints.push(limit(5))

      searchConstraints.push(where('status', '==', 'OK'))
      searchConstraints.push(where('active', '==', true))

      const constraints = [
        collection(df, `${searchCollection}/${state.getPointOfSale().id}/items`),
        ...searchConstraints
      ]

      const q = query.apply(this, constraints)

      const querySnapshot = await getDocs(q)

      const results = []
      querySnapshot.forEach(doc => {
        var item = doc.data()
        results.push({ _id: doc.id, ...item })
      }
      )

      return results
    },
    setDateDefault () {
      this.invoiceDate = Date.now()
      this.deliveryDate = Date.now()
      this.updateValueDate()
    },
    setDateToNow () {
      this.invoiceDate = Date.now()
      this.valueDate = Date.now()
      this.deliveryDate = Date.now()
    },
    showTotal () {
      if (this.paymentType.value !== 'T' && this.paymentType.value !== 'P') {
        return this.formatedTotal
      } else {
        // Za transakcijske račune i ponude:
        // Prikaži veleprodajnu cijenu (bez PDV-a) kao glavni iznos
        // Maloprodajnu cijenu (s PDV-om) iz baze prikaži u zagradama
        return this.formattedTotalVp + ' <span style="font-size: 16px">(' + this.formattedTotalMp + ')</span>'
      }
    },
    showItemPrice (item) {
      if (this.paymentType.value !== 'T' && this.paymentType.value !== 'P') {
        return this.formatNumber(
          item,
          item.data.prices[this.currency].price / 100)
      } else {
        // Za transakcijske račune i ponude:
        // Prikaži cijenu bez PDV-a kao glavni iznos
        // Cijenu s PDV-om (iz baze) prikaži u zagradama
        return this.formatNumber(
          item,
          item.data.prices[this.currency].base / 100
        ) + ' (' + this.formatNumber(
          item,
          item.data.prices[this.currency].price / 100
        ) + ')'
      }
    },
    openAvansSummary () {
      if (!this.cart[0]?.data?.name) {
        this.showMsgBox({ text: 'Potreban minimalno jedan artikl u košarici', actions: ['cancel'], cancelBtnText: 'OK', color: 'error' })
        return
      }

      let validated = true
      this.cart.forEach(it => {
        if (!this.validate(it)) {
          this.loading = false
          validated = false
          this.showMsgBox({ text: 'Podaci o proizvodu/proizvodima nepotpuni.', actions: ['cancel'], cancelBtnText: 'OK', color: 'error' })
        }
      })
      if (!validated) return

      this.avansDeducted.discount = this.discount
      this.avansDeducted.cashRegister = this.cashRegister
      this.avansDeducted.location = this.location
      this.avansDeducted.initialPayment = this.paymentType.value
      this.avansDeducted.deliveryDate = this.deliveryDate
      this.avansDeducted.note = this.note

      const avansDeductedAssociate = {}
      let associate

      if (this.associateByName) {
        associate = this.associateByName
      }
      if (this.associateByOib && typeof (this.associateByOib) !== 'string') {
        associate = this.associateByOib
      }

      if (typeof (associate) === 'string') {
        avansDeductedAssociate.payer_name = associate
        avansDeductedAssociate.payer_oib = this.associateByOib
        avansDeductedAssociate.payer_address = this.associateAddress
        avansDeductedAssociate.payer_zip_code = this.associateZip
        avansDeductedAssociate.payer_city = this.associateCity
        avansDeductedAssociate.payer_country = this.associateCountry
      } else {
        if (associate) {
          avansDeductedAssociate.payer_address = this.associateAddress
          avansDeductedAssociate.payer_city = this.associateCity
          avansDeductedAssociate.payer_country = this.associateCountry
          avansDeductedAssociate.payer_name = associate.name
          avansDeductedAssociate.payer_oib = associate.oibs && associate.oibs[0] ? associate.oibs[0] : ''
          avansDeductedAssociate.payer_zip_code = this.associateZip
        }
      }

      if (avansDeductedAssociate) {
        avansDeductedAssociate.selectedVies = this.selectedVies
        avansDeductedAssociate.selectedCustomerType = this.selectedCustomerType
        this.avansDeducted.associate = avansDeductedAssociate
      }

      this.$refs.avansSummary.open(this.avansDeducted)
    },
    openAvans () {
      this.$refs.avans.open(this.cashRegister, this.location, this.note)
    },
    async open (loader, register = undefined, initialState = null) {
      await this.setData()

      this.resetAll()

      this.user = await this.getUser()
      if (this.user) {
        if (this.userIsSalesman) {
          this.dialog = true
        } else {
          this.showMsgBoxHtml({ text: 'Nemate privilegiju prodavača na ovom prodajnom mjestu, te niste u mogućnosti izdavati račune.<br/>Da biste dodali privilegiju prodavača odaberite<br/> Postavke → Korisnici te za korisnika odaberite privilegiju "PRODAVAČ".', actions: ['cancel'], cancelBtnText: 'OK', color: 'error' })
        }
      }
      EventBus.$emit('stopButtonLoader')

      // Associates are loaded on-demand in AssociateDetailsPanel when user starts typing
      // this.getAssociates()

      if (register) {
        this.cashRegister = register

        if (this.cashRegister.search_type !== 'load') {
          this.items = []
        }
        await this.onSelectRegister()
      }

      // Vrati stanje ako je proslijeđeno (iz local storage)
      if (initialState) {
        this.$nextTick(() => {
          this.setState(initialState)
        })
      }

      // Ako nema certifikata, postavi fiskalizaciju na false
      if (!this.hasCertificate) {
        this.internalFiscalize = false
      }
    },

    close () {
      if (this.tabMode) {
        this.$emit('close', this)
      } else {
        this.resetAll()
        this.dialog = false
      }
    },

    closeTabMode () {
      this.$emit('close', this)
    },

    showPnb () {
      // (cashRegisterType === 'VP' || cashRegisterType === 'ALL')
      if (this.cashRegisterType === 'VP' || this.cashRegisterType === 'ALL') {
        if (this.paymentType.type === 'R' || this.paymentType.type === 'V' || this.paymentType.type === 'P') {
          return true
        }
      }
      return false
    },

    addHR () {
      this.pnbModel = 'HR'
    },

    getNextWorkingDay (date) {
      // Prvi radni dan se računa nakon 8 dana
      const futureDate = new Date(date)
      futureDate.setDate(futureDate.getDate() + 8)

      // Provera da li je to vikend, ako jeste, pomera na prvi radni dan
      while (futureDate.getDay() === 0 || futureDate.getDay() === 6) {
        futureDate.setDate(futureDate.getDate() + 1)
      }

      return futureDate.getTime()
    },

    formatNumber (item, number) {
      if (item.data.manual_price_input) return '- EUR'
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(
        number
      )
    },

    async onSelectLocation () {
      await this.getCashRegisters()
      // state.setPointOfSale(this.location)
    },

    async onSelectRegister () {
      // Need to refresh register
      this.cashRegisterType = this.cashRegister.type
      this.cashRegisterNumber = this.getCashRegisterNumber(this.cashRegister)

      // Učitaj register_template iz Firestore-a odmah
      const registerDoc = await df.doc(`cash_registers/${this.cashRegister.id}`).get()
      if (registerDoc.exists && registerDoc.data().register_template) {
        this.$set(this.cashRegister, 'register_template', registerDoc.data().register_template)
      }

      await this.setPaymentTypes()

      if (this.cashRegister?.search_type === 'load') {
        await this.getItems()
      }

      if (this.cashRegister?.type === 'VP' || this.cashRegister?.type === 'ALL') {
        this.calculateReferenceNumber()
      }

      if (this.registerSnapshotListener) {
        this.registerSnapshotListener()
        this.registerSnapshotListener = null
      }
      this.registerSnapshotListener = df
        .doc(`cash_registers/${this.cashRegister.id}`)
        .onSnapshot((doc) => {
          if (doc && doc.data()) {
            this.registerListener = doc.data()

            // Ažuriraj register_template na cashRegister objektu za reactive computed property
            if (this.registerListener.register_template) {
              this.$set(this.cashRegister, 'register_template', this.registerListener.register_template)
            }

            if (this.paymentType.value && this.paymentType.value === 'P') {
              this.cashRegisterNumber = this.registerListener.offer_sequence + 1
            } else if (this.paymentType.value && this.paymentType.value === 'V') {
              this.cashRegisterNumber = this.registerListener.virman_sequence + 1
            } else {
              this.cashRegisterNumber = this.registerListener.bill_sequence + 1
            }
            this.calculateReferenceNumber()
          }
        })
    },

    onSelectPaymentType () {
      if (this.paymentType.value === 'P') {
        this.cashRegisterNumber = this.getCashRegisterNumber(this.cashRegister, true, false)
        this.valueDateLabel = 'Datum važenja ponude'
      } else if (this.paymentType.value === 'V') {
        this.cashRegisterNumber = this.getCashRegisterNumber(this.cashRegister, false, true)
        this.valueDateLabel = 'Datum valute'
      } else {
        this.cashRegisterNumber = this.getCashRegisterNumber(this.cashRegister, false, false)
        this.valueDateLabel = 'Datum valute'
      }
      this.calculateReferenceNumber()
    },

    onPnbTypeChange () {
      this.calculateReferenceNumber()
    },

    resetAll () {
      this.cart = []
      this.selectedItems = []
      this.comboboxes = [{ disabled: false }]
      this.total = 0
      this.fullTotal = 0
      this.formatedTotal = '0.00'
      this.formattedTotalMp = '0.00'
      this.formattedTotalVp = '0.00'
      this.addEmptyCartItem(0)
      this.associate = undefined
      // Koristi prazan string umjesto undefined za Vuetify autocomplete
      this.associateByName = ''
      this.associateByOib = ''
      this.associateAddress = ''
      this.associateZip = ''
      this.associateCity = ''
      this.associateCountry = ''
      this.note = ''
      // this.invoiceDate = (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10)
      this.invoiceDate = Date.now()
      this.deliveryDate = Date.now()
      this.updateValueDate()
      this.issueDateTime = (Math.floor(new Date().getTime() / 1000))
      this.pnbModel = ''
      this.pnb = ''
      this.customPnb = false
      this.manualPaymentReference = ''
      this.wholeSaleRcptType = ''
      this.associatesPanel = []
      this.avansDeducted = new AvansDeducted()
      this.commentPanel = []
      this.internalFiscalize = true
      this.eracun = false
      this.eracunEmail = ''
      this.eracunAttachments = []
      this.eracunManuallyDisabled = false
      this.cashRegisters.forEach(r => {
        r.disabled = false
      })
      this.locationDisabled = false
      this.calculateReferenceNumber()
    },

    setCostCenters (associate) {
      this.costCenters = []
      if (associate && associate.cost_centers) {
        this.costCenters = Object.values(associate.cost_centers)
      }
      this.costCenters.push({ id: -1, name: 'Osnovno mjesto troška' })
    },

    onSelectAssociate () {
      // If associateByName is an object, extract the data
      if (this.associateByName && typeof this.associateByName === 'object') {
        const selectedName = this.associateByName.name

        // Set flag to prevent OIB search when name is selected
        this.isSelectingOib = true

        this.setCostCenters(this.associateByName)
        this.populateAddressFields(this.associateByName)

        if (this.associateByName?.oibs && this.associateByName.oibs.length > 0) {
          this.associateByOib = this.associateByName.oibs[0]
        } else {
          this.associateByOib = undefined
        }

        // Automatski popuni email za eRačun ako postoji
        if (this.associateByName?.email) {
          this.eracunEmail = this.associateByName.email
        }

        // Always enable saving for both new and existing clients
        this.saveAssociateEnabled = true

        // Convert associateByName to string
        this.associateByName = selectedName
      } else {
        // If it's already a string, just set the flag
        this.isSelectingOib = true
      }

      this.pnbTypes[1].disabled = false
      this.calculateReferenceNumber()

      // Clear search results after selection
      this.associates = []
    },

    onSelectAssociateOib () {
      // Check if associateByOib is an object from search results (has fullAssociate property)
      if (this.associateByOib && typeof this.associateByOib === 'object' && this.associateByOib.fullAssociate) {
        const associate = this.associateByOib.fullAssociate
        const selectedOib = this.associateByOib.oib // Store the OIB string

        // Set flag to prevent name search when OIB is selected
        this.isSelectingName = true

        // Populate all fields from search result
        this.associateByName = associate.name || this.associateByOib.name
        this.associateAddress = this.associateByOib.address || associate.address
        this.associateCity = this.associateByOib.city || associate.city
        this.associateZip = this.associateByOib.zip_code || associate.zip_code
        this.associateCountry = this.associateByOib.country || associate.country || 'Hrvatska'

        // Convert associateByOib to string (just the OIB value) BEFORE other operations
        this.associateByOib = selectedOib

        this.setCostCenters(associate)

        // Automatski popuni email za eRačun ako postoji
        if (associate?.email) {
          this.eracunEmail = associate.email
        }

        // Always enable saving for both new and existing clients
        this.saveAssociateEnabled = true
      } else if (this.associateByOib && typeof this.associateByOib === 'object') {
        // Original logic for objects without fullAssociate (from default associates list)
        this.isSelectingName = true
        this.setCostCenters(this.associateByOib)

        this.populateAddressFields(this.associateByOib)
        this.associateByName = this.associateByOib?.name ?? undefined

        // Automatski popuni email za eRačun ako postoji
        if (this.associateByOib?.email) {
          this.eracunEmail = this.associateByOib.email
        }

        // Always enable saving for both new and existing clients
        this.saveAssociateEnabled = true
      } else {
        // If it's already a string, just set the flag
        this.isSelectingName = true
      }

      this.pnbTypes[1].disabled = false
      this.calculateReferenceNumber()

      // Clear search results after selection
      this.oibOptions = []
    },

    populateAddressFields (collection) {
      this.associateAddress = collection?.address ?? undefined
      this.associateZip = collection?.zip_code ?? undefined
      this.associateCity = collection?.city ?? undefined
      this.associateCountry = collection?.country ?? undefined
    },

    clearAssociateFields () {
      this.associateByName = ''
      this.associateByOib = ''
      this.associateAddress = ''
      this.associateZip = ''
      this.associateCity = ''
      this.associateCountry = ''
      this.selectedCostCenter = null
      this.costCenters = []
      this.saveAssociateEnabled = false
      this.doSaveAccount = false
      // Clear search results
      this.associates = []
      this.oibOptions = []
    },

    canAddNewItem () {
      let canAddNewItem = false
      for (let i = 0; i < this.cart.length; i++) {
        const hasTax = this.cart[i].tax && typeof this.cart[i].tax === 'object' && this.cart[i].tax.rate !== undefined
        if (!this.cart[i].data?.name || !this.cart[i].unit || !hasTax || !this.cart[i].price) {
          canAddNewItem = true
        }
      }

      return canAddNewItem
    },

    getItemValidationErrors () {
      // Vraća objekt sa informacijama o nedostajućim poljima za svaki item
      const errors = {}
      for (let i = 0; i < this.cart.length; i++) {
        errors[i] = {
          name: !this.cart[i].data?.name,
          unit: !this.cart[i].unit,
          tax: !this.cart[i].tax || typeof this.cart[i].tax !== 'object' || this.cart[i].tax.rate === undefined,
          price: !this.cart[i].price || this.cart[i].price <= 0
        }
      }
      return errors
    },

    addEmptyCartItem (mode = 1) {
      let canAddNewItem = true
      for (let i = 0; i < this.cart.length; i++) {
        const item = this.cart[i]
        const hasTax = item.tax && typeof item.tax === 'object' && item.tax.rate !== undefined
        const hasName = item.data?.name && item.data.name.trim() !== ''
        const hasUnit = item.unit !== null && item.unit !== undefined
        const hasPrice = item.price !== null && item.price !== undefined && item.price > 0

        // Za free item koji se još kuca, provjeravamo da li ima sve podatke
        if (item.data?.freeItem && hasName) {
          const formattedPriceValue = item.formattedPrice ? parseFloat(item.formattedPrice.toString().replace(',', '.')) : 0
          const hasFormattedPrice = formattedPriceValue > 0
          const hasValidPrice = hasPrice || hasFormattedPrice

          if (!hasTax || !hasValidPrice) {
            canAddNewItem = false
          }
        } else if (!hasName || !hasUnit || !hasTax || !hasPrice) {
          canAddNewItem = false
        }
      }

      if (!canAddNewItem) return

      // Inicijaliziraj novi cart item sa svim potrebnim poljima
      const newItem = {
        data: {
          name: '',
          type: undefined,
          freeItem: true // Omogući PDV dropdown
        },
        tax: null, // Nema defaultnog PDV-a, korisnik mora odabrati
        unit: null,
        units: [],
        quantity: 1,
        price: 0,
        discount: 0,
        kpdcode: '',
        taxCategory: null,
        taxExemptionReason: null,
        formattedQuantity: '1.00',
        formattedPrice: '0.00',
        formattedDiscount: '0.00',
        formattedVpcPrice: '0.00'
      }

      this.cart.push(newItem)
      this.comboboxes.push({ disabled: false })
      this.selectedItems.push({ data: { name: '', id: '', searchName: '' } })
      this.searchKeywords.push('')

      // Fokusiraj novi item nakon što se renderira
      this.$nextTick(() => {
        const newIndex = this.cart.length - 1

        // Fokusiraj novi item
        if (this.$refs.cartItemsList && this.$refs.cartItemsList.focusItem) {
          this.$refs.cartItemsList.focusItem(newIndex)
        }

        // Scroll do total-a ako je potrebno
        if (this.$refs.cartItemsList && this.$refs.cartItemsList.$refs.formatted_total && mode === 1) {
          this.$refs.cartItemsList.$refs.formatted_total.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
    },

    removeCartItem (counter) {
      // Ne daj obrisati jedini item na računu
      const tempCart = clone(this.cart)

      this.cart = []
      if (tempCart.length > 1) {
        this.comboboxes.splice(counter, 1)
        tempCart.splice(counter, 1)
        this.selectedItems.splice(counter, 1)
        tempCart.forEach(item => {
          this.cart.push({ ...item })
        })
      } else {
        this.resetAll()
      }
      this.calculateTotals()
    },

    prepareItemForManualInput (name, index) {
      const itemId = uuidv4()
      const prices = {
        EUR: {
          price: 0,
          taxes: [],
          active: true
        }
      }
      prices.EUR.price = 0

      const item = {
        data: {
          id: itemId,
          active: true,
          status: 'OK',
          name: name,
          manual_price_input: true,
          prices: prices,
          type: this.cart[index].data?.type || 'SERVICE',
          freeItem: true,
          searchName: name
        },
        unitsAndPrices: []
      }

      // Ako su ova polja već popunjena u formi, prekopiraj ih ovdje
      if (this.cart[index].unitsAndPrices) {
        item.unitsAndPrices = this.cart[index].unitsAndPrices
      }
      if (this.cart[index].unit) {
        item.unit = this.cart[index].unit
      }
      if (this.cart[index].tax) {
        item.tax = { ...this.cart[index].tax }
        const tax = { ...item.tax }
        tax.rate *= 10000
        prices[this.currency].taxes.push(tax)
      }
      if (this.cart[index].quantity) {
        item.quantity = this.cart[index].quantity
      }
      if (this.cart[index].formattedQuantity) {
        item.formattedQuantity = this.cart[index].formattedQuantity
      }
      if (this.cart[index].price) {
        item.price = this.cart[index].price
        prices[this.currency].price = item.price * 100
      }

      if (this.cart[index].formattedPrice) {
        item.formattedPrice = this.cart[index].formattedPrice
      }

      if (this.cart[index].discountAmt) {
        item.discountAmt = this.cart[index].discountAmt
      }
      if (this.cart[index].formattedDiscount) {
        item.formattedDiscount = this.cart[index].formattedDiscount
      }

      if (this.cart[index].manualPrice) {
        item.manualPrice = this.cart[index].manualPrice
      }

      if (this.cart[index].pricePdv) {
        item.pricePdv = this.cart[index].pricePdv
      }

      if (this.cart[index].priceBase) {
        item.priceBase = this.cart[index].priceBase
      }

      // Sačuvaj kpdcode, taxCategory i taxExemptionReason ako postoje
      if (this.cart[index].kpdcode) {
        item.kpdcode = this.cart[index].kpdcode
      }
      if (this.cart[index].taxCategory) {
        item.taxCategory = this.cart[index].taxCategory
      }
      if (this.cart[index].taxExemptionReason) {
        item.taxExemptionReason = this.cart[index].taxExemptionReason
      }

      return item
    },

    handleSelectedItemUpdate (index, value) {
      // Ne pozivaj selectItem ako je prazan string
      if (!value) {
        this.selectedItems[index] = value
        this.cartVersion++ // Trigger re-compute
        return
      }

      // Ako je objekat (odabrano iz liste), odmah pozovi selectItem
      if (typeof value === 'object') {
        this.selectedItems[index] = value
        this.selectItem(index)
        this.cartVersion++ // Trigger re-compute
        return
      }

      // Ako je string (manual input), NE pozivaj selectItem dok korisnik tipka
      // Samo čuvaj string u selectedItems - selectItem će se pozvati na blur
      if (typeof value === 'string' && value.trim() !== '') {
        this.selectedItems[index] = value

        // Ažuriraj cart[index].data.name da se prikazuje u UI
        if (!this.cart[index].data) {
          this.cart[index].data = {}
        }
        this.cart[index].data.name = value
        this.cart[index].data.freeItem = true
        this.cartVersion++ // Trigger re-compute
      }
    },

    handleItemChange (index, value) {
      // @change se poziva kada combobox finalizira izbor (blur ili enter)
      // Ovdje treba pozvati selectItem
      if (!value) return

      // Ako je string, to je manual input - treba ga pretvoriti u item
      if (typeof value === 'string' && value.trim() !== '') {
        this.selectedItems[index] = value
        this.selectItem(index)
        return
      }

      // Ako je objekat, item je već odabran iz liste
      if (typeof value === 'object') {
        this.selectedItems[index] = value
        this.selectItem(index)
      }
    },

    selectItem (index) {
      if (!this.selectedItems[index]) return
      // Kod ručnog unosa vrati string, a kod odabira object
      if (typeof (this.selectedItems[index]) === 'string') {
        this.selectedItems[index] = this.prepareItemForManualInput(this.selectedItems[index], index)
      }
      this.cart[index] = clone(this.selectedItems[index])

      // Initialize kpdcode from the item data if it exists
      if (this.cart[index].data && this.cart[index].data.kpdcode) {
        this.$set(this.cart[index], 'kpdcode', this.cart[index].data.kpdcode)
      } else {
        this.$set(this.cart[index], 'kpdcode', '')
      }

      // Initialize taxCategory and taxExemptionReason
      if (!this.cart[index].taxCategory) {
        this.$set(this.cart[index], 'taxCategory', null)
      }
      if (!this.cart[index].taxExemptionReason) {
        this.$set(this.cart[index], 'taxExemptionReason', null)
      }

      // Initialize formattedVpcPrice if not present
      if (!this.cart[index].formattedVpcPrice) {
        this.$set(this.cart[index], 'formattedVpcPrice', '0.00')
      }

      this.updateCartItem(index)
      this.checkEracunAvailability()
    },

    updateQuantity (index) {
      // Zamijeni zarez sa točkom prije parsiranja
      const value = String(this.cart[index].formattedQuantity).replace(',', '.')
      this.cart[index].quantity = Number(value)
      this.cart[index].formattedQuantity = this.cart[index].quantity.toFixed(2).replace('.', ',')
      this.updateCartItem(index)
    },

    updatePrice (index) {
      // Zamijeni zarez sa točkom prije parsiranja
      const value = String(this.cart[index].formattedPrice).replace(',', '.')
      this.cart[index].manualPrice = Number(value)
      this.cart[index].price = this.cart[index].manualPrice

      // if ((this.paymentType.value === 'T' || this.paymentType.value === 'P') && this.cart[index].tax) {
      //   this.cart[index].manualPrice *= (1 + this.cart[index].tax.rate)
      // }

      this.cart[index].formattedPrice = this.cart[index].price.toFixed(2).replace('.', ',')

      this.updateCartItem(index)
    },

    updateVpcPrice (index) {
      // Calculate MPC from VPC: MPC = VPC * (1 + PDV_stopa)
      if (!this.cart[index].formattedVpcPrice || !this.cart[index].tax) {
        return
      }

      // Parse VPC value (already in dot format from number input)
      const vpcPrice = Number(this.cart[index].formattedVpcPrice)
      const taxRate = this.cart[index].tax.rate // Already in decimal format (e.g., 0.25 for 25%)

      // Calculate MPC from VPC
      const mpcPrice = vpcPrice * (1 + taxRate)

      // Update MPC price
      this.cart[index].manualPrice = mpcPrice
      this.cart[index].price = mpcPrice
      this.cart[index].formattedPrice = mpcPrice.toFixed(2)

      this.updateCartItem(index)
    },

    updateDiscount (index) {
      // Zamijeni zarez sa točkom prije parsiranja
      const value = String(this.cart[index].formattedDiscount).replace(',', '.')
      this.cart[index].discountAmt = Number(value)
      this.cart[index].formattedDiscount = this.cart[index].discountAmt.toFixed(2).replace('.', ',')
      this.updateCartItem(index)
    },

    applyMassDiscount (discountPercent) {
      // Apply discount to all cart items
      if (!discountPercent || discountPercent <= 0) {
        return
      }

      // Iterate through all cart items and set discount
      this.cart.forEach((item, index) => {
        // Skip empty items
        if (!item.data || !item.data.name) {
          return
        }

        // Set discount amount and formatted value
        this.cart[index].discountAmt = Number(discountPercent)
        this.cart[index].formattedDiscount = this.cart[index].discountAmt.toFixed(2).replace('.', ',')

        // Trigger recalculation for this item
        this.updateCartItem(index)
      })
    },

    updateCartItem (index) {
      // Inkrementiraj cartVersion da triggeruješ computed properties
      this.cartVersion++

      // Ako artikl nema količinu, stavi 1
      if (!this.cart[index].quantity) {
        this.cart[index].quantity = 1
        this.cart[index].fomattedQuantity = '1,00'
      }

      // Ako artik nema popust, stavi 0
      if (!this.cart[index].discountAmt) {
        this.cart[index].discountAmt = 0
        this.cart[index].formattedDiscount = '0,00'
      }

      // Ovo je bio bug ako uzmes mjernu jedinicu koja nije defaultna,
      // onda cijena ostane kriva i validator ne da izdati racun
      // ALI, ne prebrisuj prices za freeItem jer gubimo taxes i active
      if (this.cart[index].unit?.price && !this.cart[index].data?.freeItem) {
        this.cart[index].data.prices = this.cart[index].unit?.price
      }

      if (this.cart[index].data && this.cart[index].unitsAndPrices) {
        this.cart[index].units = this.cart[index].unitsAndPrices

        const taxes = this.cart[index].data.prices[this.currency].taxes
        if (taxes && taxes.length > 0) {
          if (!this.cart[index].tax) {
            const tax = { ...taxes[0] }
            if (tax) {
              tax.rate /= 10000
              this.cart[index].tax = tax
            }
          }
        }

        const defaultSalesUnit = this.cart[index].data.warehouse_units?.default_sale
        if (defaultSalesUnit) {
          const unit = this.cart[index].units.find(unit => unit.id === defaultSalesUnit)
          if (!this.cart[index].unit) {
            if (unit) {
              this.cart[index].unit = unit
              if (unit.price[this.currency]?.price) {
                if (!this.cart[index].manualPrice) {
                  this.cart[index].pricePdv = clone(unit.price[this.currency].price)
                  this.cart[index].priceBase = clone(unit.price[this.currency].base)

                  this.cart[index].price = unit.price[this.currency].price / 100
                }
              }
            } else {
              if (this.cart[index].units?.length > 0) {
                const unit = this.cart[index].units[0]
                this.cart[index].unit = unit

                if (!this.cart[index].manualPrice) {
                  this.cart[index].pricePdv = clone(unit.price[this.currency].price)
                  this.cart[index].priceBase = clone(unit.price[this.currency].base)

                  this.cart[index].price = unit.price[this.currency].price / 100

                  this.cart[index].data.prices[this.currency].base = clone(unit.price[this.currency].base)
                  this.cart[index].data.prices[this.currency].price = clone(unit.price[this.currency].price)
                }
              }
            }
          } else {
            if (!this.cart[index].manualPrice) {
              this.cart[index].pricePdv = clone(this.cart[index].unit.price[this.currency].price)
              this.cart[index].priceBase = clone(this.cart[index].unit.price[this.currency].base)

              this.cart[index].price = this.cart[index].unit.price[this.currency].price / 100
            }
          }
        }

        // Dodaj popust
        this.cart[index].data.discounted_price = 0

        if (this.cart[index].discountAmt !== undefined && this.cart[index].discountAmt > 0) {
          this.cart[index].data.discounted_price = Number(this.calculateDiscount(this.cart[index]).toFixed(2))

          if (!this.cart[index].manualPrice) {
            this.cart[index].data.single_item_price = this.cart[index].data.prices[this.currency].price
          } else {
            this.cart[index].data.single_item_price = this.cart[index].manualPrice * 100
          }

          this.cart[index].data.discount = {
            active: true,
            amount: this.cart[index].data.single_item_price - this.cart[index].data.discounted_price * 100,
            name: 'Popust',
            rate: Number(this.cart[index].discountAmt) * 100,
            type: 'RATE'
          }

          // Set total with discounted price and quantity (in cents)
          this.cart[index].data.total = Math.round(this.cart[index].data.discounted_price * this.cart[index].quantity * 100)
        } else {
          // No discount - set total with regular price
          this.cart[index].data.total = Math.round(this.cart[index].price * this.cart[index].quantity * 100)
        }
      } else {
        // Firma nema skladište
        if (this.cart[index].data.prices[this.currency]?.active) {
          if (!this.cart[index].manualPrice) {
            this.cart[index].pricePdv = clone(this.cart[index].data.prices[this.currency].price)
            this.cart[index].priceBase = clone(this.cart[index].data.prices[this.currency].base)

            this.cart[index].price = this.cart[index].data.prices[this.currency].price / 100
          }
          const taxRate = this.cart[index].data.prices[this.currency].taxes[0].rate / 10000
          if (taxRate !== undefined && taxRate !== null) {
            this.cart[index].tax = this.taxes.find(item => item.rate === taxRate)
          }
          if (this.cart[index].data.selling_unit) {
            this.cart[index].unit = ({ storageUnit: this.cart[index].data.selling_unit })
          }

          // Dodaj popust
          this.cart[index].data.discounted_price = 0
          if (this.cart[index].discountAmt !== undefined && this.cart[index].discountAmt > 0) {
            this.cart[index].data.discounted_price = Number(this.calculateDiscount(this.cart[index]).toFixed(2))

            if (!this.cart[index].manualPrice) {
              this.cart[index].data.single_item_price = this.cart[index].data.prices[this.currency].price
            } else {
              this.cart[index].data.single_item_price = this.cart[index].manualPrice * 100
            }

            this.cart[index].data.discount = {
              active: true,
              amount: this.cart[index].data.single_item_price - this.cart[index].data.discounted_price * 100,
              name: 'Popust',
              rate: Number(this.cart[index].discountAmt) * 100,
              type: 'RATE'
            }

            // Set total with discounted price and quantity (in cents)
            this.cart[index].data.total = Math.round(this.cart[index].data.discounted_price * this.cart[index].quantity * 100)
          } else {
            // No discount - set total with regular price
            this.cart[index].data.total = Math.round(this.cart[index].price * this.cart[index].quantity * 100)
          }
        }
      }

      // pricePdv i priceBase su UVIJEK postavljeni iz baze gore (linije 1785-1786, 1797-1798, 1809-1810, 1841-1842)
      // Ne trebamo nikakvu dodatnu logiku

      this.cart[index].formattedPrice = this.cart[index].price ? this.cart[index].price.toFixed(2) : ''
      this.cart[index].formattedQuantity = this.cart[index].quantity ? this.cart[index].quantity.toFixed(2) : ''
      this.cart[index].formattedDiscount = this.cart[index].discountAmt ? this.cart[index].discountAmt.toFixed(2) : ''

      // Calculate VPC from MPC: VPC = MPC / (1 + PDV_stopa)
      if (this.cart[index].price && this.cart[index].tax && this.cart[index].tax.rate !== undefined) {
        const mpcPrice = this.cart[index].price
        const taxRate = this.cart[index].tax.rate // Already in decimal format (e.g., 0.25 for 25%)
        const vpcPrice = mpcPrice / (1 + taxRate)
        this.cart[index].formattedVpcPrice = vpcPrice.toFixed(2)
      } else {
        this.cart[index].formattedVpcPrice = '0.00'
      }

      const firstItemIsInserted = this.cart[0].price && this.cart[0].quantity && this.cart[0].data.name
      this.cashRegisters.forEach(r => {
        r.disabled = firstItemIsInserted && r.type !== this.cashRegister.type
      })
      this.locationDisabled = !!firstItemIsInserted

      this.avansDeducted.items = []
      this.cart.forEach((itm, i) => {
        var it = clone(itm)
        var item = clone(it.data)

        item.quantity = it.quantity

        item.discounted_price = it.data.discounted_price
        item.price = it.price
        item.total = it.price * 100
        item.discounted_price *= 100

        // Recalculate total with quantity and discount
        if (it.data.discount && item.discounted_price) {
          item.total = item.discounted_price * it.quantity
        } else {
          item.total = it.price * it.quantity * 100
        }

        item.prices[this.currency].price = item.price * 100

        if (it.data.single_item_price > 0) {
          item.unitPrice = it.data.single_item_price
        }

        if (this.paymentType.value === 'T' || this.paymentType.value === 'P') {
          if (item.discount?.amount) {
            item.discount.amount = (it.price - itm.data.discounted_price) * 100
          }

          item.single_item_price = item.price
        }

        if (it.tax) {
          if (item.prices[this.currency].taxes && item.prices[this.currency].taxes.length === 0) {
            var newTax = {
              name: it.tax.name,
              rate: it.tax.rate * 10000
            }
            item.prices[this.currency].taxes = [newTax]
            this.cart[i].data.prices[this.currency].taxes = [newTax]
          } else {
            item.prices[this.currency].taxes[0].name = it.tax.name
            item.prices[this.currency].taxes[0].rate = it.tax.rate * 10000

            this.cart[i].data.prices[this.currency].taxes[0].rate = it.tax.rate * 10000
          }
        }

        item.prices[this.currency].price = Math.round(item.prices[this.currency].price)

        this.avansDeducted.items.push(item)
      })

      this.calculateTotals()
    },

    calculateTotals () {
      // Izračunaj totale grupiranjem po PDV-u
      var totalMp = 0
      var totalVp = 0
      var fullTotalMp = 0
      var fullTotalVp = 0
      var pdvs = []

      this.cart.forEach((item, i) => {
        if (this.isDefined(item.quantity) && this.isDefined(item.price) && this.isDefined(item.tax)) {
          // Grupiraj po PDV-u
          var pdvName = 'p' + parseInt(item.tax.rate * 10000)
          if (!pdvs[pdvName]) {
            pdvs[pdvName] = {
              base: 0,
              pdv: 0,
              total: 0,
              rate: item.tax.rate * 10000
            }
          }

          // Odredi cijenu za ovaj item (s ili bez popusta)
          let itemPriceWithVat, itemPriceBase

          if (this.isDefined(item.data.discounted_price)) {
            // S popustom - discounted_price je već s PDV-om
            itemPriceWithVat = Math.round(item.data.discounted_price * 100 * item.quantity)
            itemPriceBase = Math.round(itemPriceWithVat / (1 + item.tax.rate))
          } else {
            // Bez popusta - item.price je s PDV-om
            itemPriceWithVat = Math.round(item.price * 100 * item.quantity)
            itemPriceBase = Math.round(itemPriceWithVat / (1 + item.tax.rate))
          }

          // Akumuliraj u PDV grupu
          pdvs[pdvName].base += itemPriceBase
          pdvs[pdvName].total += itemPriceWithVat

          // Dodaj u fullTotal (bez popusta - uvijek koristi item.price)
          fullTotalMp += item.price * 100 * item.quantity
          fullTotalVp += (item.price * 100 * item.quantity) / (1 + item.tax.rate)

          if (item.discount?.amount > 0) {
            item.discount.amount = fullTotalMp - totalMp
          }
        }
      })

      // Zaokruži svaku PDV grupu
      Object.keys(pdvs).forEach(pdvKey => {
        pdvs[pdvKey].base = Math.round(pdvs[pdvKey].base)
        pdvs[pdvKey].total = Math.round(pdvs[pdvKey].total)
        pdvs[pdvKey].pdv = pdvs[pdvKey].total - pdvs[pdvKey].base
      })

      // Izračunaj ukupne totale IZ PDV grupa (ne artikl-po-artikl)
      totalVp = Object.keys(pdvs).reduce((sum, key) => sum + pdvs[key].base, 0)
      totalMp = Object.keys(pdvs).reduce((sum, key) => sum + pdvs[key].total, 0)

      fullTotalMp = Math.round(fullTotalMp)
      fullTotalVp = Math.round(fullTotalVp)
      if (isNaN(totalVp)) { totalVp = 0 }
      if (isNaN(totalMp)) { totalMp = 0 }
      if (isNaN(fullTotalVp)) { fullTotalVp = 0 }
      if (isNaN(fullTotalMp)) { fullTotalMp = 0 }
      this.avansDeducted.totalMp = totalMp
      this.avansDeducted.totalVp = totalVp
      this.avansDeducted.fullTotalVp = fullTotalVp
      this.avansDeducted.fullTotalMp = fullTotalMp
      this.avansDeducted.totalDiscountPdv = fullTotalMp - totalMp
      this.avansDeducted.formattedTotalDiscountPdv = (this.avansDeducted.totalDiscountPdv / 100).toFixed(2).replace('.', ',')

      this.avansDeducted.pdvs = pdvs
      this.formattedTotalVp = (totalVp / 100).toFixed(2).replace('.', ',')
      this.formattedTotalMp = (totalMp / 100).toFixed(2).replace('.', ',')

      // Postavi this.total i this.fullTotal koje koristi templatePayment.js
      // (u centima, jer payment.js radi samo Math.round bez množenja)
      this.fullTotal = fullTotalMp
      this.total = totalMp
      if (isNaN(this.total)) { this.total = 0 }
      this.formatedTotal = (this.total / 100).toFixed(2).replace('.', ',')
    },

    isDefined (param) {
      if (param === undefined) {
        return false
      }

      if (param === null) {
        return false
      }

      if (param === 0) {
        return false
      }

      return true
    },

    calculateDiscount (item) {
      // Radi izračun u centima da izbjegne floating-point greške
      if (!item.manualPrice) {
        const priceInCents = Math.round(item.price * 100)
        const discountedInCents = Math.round(priceInCents * (100 - item.discountAmt) / 100)
        return discountedInCents / 100
      }
      const manualPriceInCents = Math.round(item.manualPrice * 100)
      const discountedInCents = Math.round(manualPriceInCents * (100 - item.discountAmt) / 100)
      return discountedInCents / 100
    },

    getCategory (item) {
      var categories = ''
      if (item.data.categories && item.data.categories.length > 0) {
        item.data.categories.forEach(cat => {
          categories += cat.name.toUpperCase() + ' | '
        })
      }
      return categories.replace(/\|([^|]*)$/, '$1')
    },

    formatDate (date) {
      if (!date) return null
      const [year, month, day] = date.split('-')
      return `${day}.${month}.${year}`
    },

    parseDate (date) {
      if (!date) return null
      const [month, day, year] = date.split('.')
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
    },

    detachListeners () {
      this.listeners.forEach((listener) => {
        listener()
        this.listeners = this.listeners.filter((l) => l !== listener)
      })
    },

    getItems () {
      this.isLoading = true
      this.items = []
      if (!this.location.id) { return }
      df.collection(`location_pricelist/${this.location.id}/items`)
        .where('status', '==', 'OK')
        .where('active', '==', true)
        .where(`prices.${this.currency}.active`, '==', true)
        .get()
        .then((documentSnapshots) => {
          if (documentSnapshots.docs && !documentSnapshots.docs.empty) {
            documentSnapshots.docs.forEach((it) => {
              var item = {}
              item.data = it.data()
              item = this.setupItem(item)
              if (item.data.active) {
                this.items.push(item)
              }
            })
          }
        }).catch(() => {
          console.error('An error has occured')
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    setupItem (item) {
      if (!item.data.prices || !item.data.prices.EUR) return item
      item.data.searchName = item.data.name + ' ' + item.data.code
      // TODO remove after script adds base to all
      if (item.data.prices.EUR?.base === null || item.data.prices.EUR?.base === undefined) {
        item.data.prices.EUR.base = Math.round(item.data.prices.EUR.price / (1 + (item.data.prices.EUR.taxes[0].rate / 10000)))
      }

      if (item.data.warehouse_units?.units) {
        item.unitsAndPrices = []
        Object.keys(item.data.warehouse_units.units).forEach(key => {
          const units = item.data.warehouse_units.units[key]
          if (units?.storage_unit && units?.prices && units?.prices[this.currency]?.active) {
            if (item.data.manual_price_input) {
              units.prices[this.currency].price = '0'
            }

            // TODO remove after script adds base to all
            if (units.prices && units.prices.EUR && (units.prices.EUR?.base === null || units.prices.EUR?.base === undefined)) {
              units.prices.EUR.base = Math.round(units.prices.EUR.price / (1 + (units.prices.EUR.taxes[0].rate / 10000)))
            }

            item.unitsAndPrices.push({ id: key, storageUnit: units.storage_unit, price: units.prices })
          }
        })
      }

      return item
    },

    async getAssociates () {
      // Associates are now loaded on-demand via search in AssociateDetailsPanel
      // This method is kept for backwards compatibility but does nothing
    },

    onOibSearch (searchValue) {
      if (this.isSelectingOib) {
        this.isSelectingOib = false
        return
      }

      clearTimeout(this.oibSearchTimer)

      if (!searchValue || searchValue.length < 3) {
        this.oibOptions = []
        this.lastOibSearchHadResults = true
        this.lastOibSearchQuery = ''
        return
      }

      const isExtendingPreviousQuery = this.lastOibSearchQuery &&
                                       searchValue.startsWith(this.lastOibSearchQuery) &&
                                       searchValue.length > this.lastOibSearchQuery.length

      // Exception: Always search when reaching 11 digits for businessentities fallback
      const isFullOib = searchValue.length === 11 && /^\d{11}$/.test(searchValue)

      if (isExtendingPreviousQuery && !this.lastOibSearchHadResults && !isFullOib) {
        return // Skip search
      }

      this.oibSearchTimer = setTimeout(async () => {
        await this.searchAssociatesByOib(searchValue)
      }, 300)
    },

    onNameSearch (searchValue) {
      if (this.isSelectingName) {
        this.isSelectingName = false
        return
      }

      clearTimeout(this.nameSearchTimer)

      if (!searchValue || searchValue.length < 3) {
        this.associates = []
        this.lastNameSearchHadResults = true
        this.lastNameSearchQuery = ''
        return
      }

      const isExtendingPreviousQuery = this.lastNameSearchQuery &&
                                       searchValue.startsWith(this.lastNameSearchQuery) &&
                                       searchValue.length > this.lastNameSearchQuery.length

      if (isExtendingPreviousQuery && !this.lastNameSearchHadResults) {
        return // Skip search
      }

      this.nameSearchTimer = setTimeout(async () => {
        await this.searchAssociatesByName(searchValue)
      }, 300)
    },

    async searchAssociatesByOib (oibQuery) {
      if (!oibQuery || oibQuery.length < 3) {
        this.oibOptions = []
        return
      }

      try {
        this.loadingOib = true
        const path = `/associates/${state.getCurrentCompany().id}/associate?filter=trigram(eq:${encodeURIComponent(oibQuery)})`
        const response = await callAssociates('get', path)

        if (response && response.status === 200 && response.data && response.data.associates) {
          const associatesList = Object.keys(response.data.associates)
            .map((key) => response.data.associates[key])
            .filter((associate) => associate.status === 'OK')

          const oibOptionsArray = []
          associatesList.forEach(associate => {
            if (associate.oibs && associate.oibs.length > 0) {
              associate.oibs.forEach(oib => {
                if (oib.toLowerCase().includes(oibQuery.toLowerCase())) {
                  oibOptionsArray.push({
                    oib: oib,
                    name: associate.name,
                    address: associate.address,
                    city: associate.city,
                    zip_code: associate.zip_code,
                    country: associate.country,
                    fullAssociate: associate
                  })
                }
              })
            }
          })

          this.oibOptions = oibOptionsArray

          // If no results and OIB is exactly 11 digits, try businessentities endpoint
          if (oibOptionsArray.length === 0 && oibQuery.length === 11 && /^\d{11}$/.test(oibQuery)) {
            const businessResponse = await callBusinessEntities(oibQuery)

            if (businessResponse && businessResponse.status === 200 && businessResponse.data && businessResponse.data.data) {
              const responseData = businessResponse.data.data
              if (responseData.code === 200 && responseData.business_entity) {
                const entity = responseData.business_entity
                const entityOption = {
                  oib: entity.oib || oibQuery,
                  name: entity.name || entity.shortName || '',
                  address: entity.headquatersAddress || '',
                  city: entity.headquatersCity || '',
                  zip_code: entity.headquatersZip || '',
                  country: 'Hrvatska',
                  fullAssociate: {
                    name: entity.name || entity.shortName || '',
                    oibs: [entity.oib || oibQuery],
                    address: entity.headquatersAddress || '',
                    city: entity.headquatersCity || '',
                    zip_code: entity.headquatersZip || '',
                    country: 'Hrvatska',
                    status: 'OK'
                  }
                }
                this.oibOptions = [entityOption]
              }
            }
          }

          this.lastOibSearchHadResults = this.oibOptions.length > 0
          this.lastOibSearchQuery = oibQuery
        } else {
          this.oibOptions = []
          this.lastOibSearchHadResults = false
          this.lastOibSearchQuery = oibQuery
        }
      } catch (error) {
        console.error('Error searching associates by OIB:', error)
        this.oibOptions = []
        this.lastOibSearchHadResults = false
        this.lastOibSearchQuery = oibQuery
      } finally {
        this.loadingOib = false
      }
    },

    async searchAssociatesByName (nameQuery) {
      if (!nameQuery || nameQuery.length < 3) {
        this.associates = []
        return
      }

      try {
        this.loadingName = true
        const path = `/associates/${state.getCurrentCompany().id}/associate?filter=trigram(eq:${encodeURIComponent(nameQuery)})`
        const response = await callAssociates('get', path)

        if (response && response.status === 200 && response.data && response.data.associates) {
          this.associates = Object.keys(response.data.associates)
            .map((key) => response.data.associates[key])
            .filter((associate) => associate.status === 'OK')
            .sort((a, b) => a.name.localeCompare(b.name))

          this.lastNameSearchHadResults = this.associates.length > 0
          this.lastNameSearchQuery = nameQuery
        } else {
          this.associates = []
          this.lastNameSearchHadResults = false
          this.lastNameSearchQuery = nameQuery
        }
      } catch (error) {
        console.error('Error searching associates by name:', error)
        this.associates = []
        this.lastNameSearchHadResults = false
        this.lastNameSearchQuery = nameQuery
      } finally {
        this.loadingName = false
      }
    },

    getMultiplier () {
      const listener = df.doc('project_constants/config').onSnapshot(doc => {
        const data = doc.data()
        if (data && data.item_amount && data.item_amount.decimal_multiplier !== undefined) {
          this.config = data
          this.multiplier = data.item_amount.decimal_multiplier
        }
      })
      this.listeners.push(listener)
    },

    async setData () {
      await this.getPointsOfSale()
      this.selectedPrinter = state.getSelectedPrinter()
    },

    setPaymentTypes () {
      const company = state.getCurrentCompany()
      const hasEracunProvider = company?.eracun?.provider && company?.eracun?.active === true
      const transakcijskiLabel = hasEracunProvider ? 'Transakcijski / eRačun' : 'Transakcijski račun'

      if (this.cashRegisterType === 'VP') {
        this.paymentTypes = [
          { name: transakcijskiLabel, value: 'T', type: 'R' },
          { name: 'Ponuda', value: 'P', type: 'P' },
          { name: 'Virman', value: 'V', type: 'V' }
        ]
        this.paymentType = { name: transakcijskiLabel, value: 'T', type: 'R' }
      } else if (this.cashRegisterType === 'ALL') {
        this.paymentTypes = [
          { name: transakcijskiLabel, value: 'T', type: 'R' },
          { name: 'Ponuda', value: 'P', type: 'P' },
          { name: 'Virman', value: 'V', type: 'V' }
        ]

        this.paymentType = { name: transakcijskiLabel, value: 'T', type: 'R' }

        if (company.certificate !== undefined && company.certificate !== null && company.certificate !== '') {
          this.paymentTypes = [
            { name: transakcijskiLabel, value: 'T', type: 'R' },
            { name: 'Ponuda', value: 'P', type: 'P' },
            { name: 'Gotovina', value: 'G', type: 'BO' },
            { name: 'Kartice', value: 'K', type: 'BO' },
            { name: 'Ostalo', value: 'O', type: 'BO' },
            { name: 'Virman', value: 'V', type: 'V' }
          ]
          this.paymentType = { name: 'Gotovinski račun', value: 'G', type: 'BO' }
        }
      } else {
        this.paymentTypes = [
          { name: 'Gotovina', value: 'G', type: 'BO' },
          { name: 'Kartice', value: 'K', type: 'BO' },
          { name: 'Ostalo', value: 'O', type: 'BO' }
        ]
        this.paymentType = { name: 'Gotovina', value: 'G', type: 'BO' }
      }
    },

    async getUser () {
      let user
      const doc = await df.doc(`users/${auth.currentUser.uid}`).get()
      if (doc && doc.data()) {
        user = doc.data()
        this.roles = Object.keys(user.roles)
        if (this.roles.includes('PRODAVAČ')) {
          this.userIsSalesman = true
        }
      }
      return user
    },

    rValidation () {
      if (!this.associateByName && !this.associateByOib) {
        this.confirm({
          title: 'Kupac nije odabran',
          message: 'Želite li izdati račun bez podataka o kupcu?',
          options: {
            toolbar: true,
            width: 410,
            confirmText: 'Da, izdaj račun',
            cancelText: 'Odustani'
          }
        }).then((confirmed) => {
          if (confirmed) {
            this.confirmClose()
            this.isCreatingReceipt = true
            this.createReceipt().catch(err => {
              console.error('Error creating receipt:', err)
              this.isCreatingReceipt = false
            })
          }
        })
      } else {
        this.isCreatingReceipt = true
        this.createReceipt().catch(err => {
          console.error('Error creating receipt:', err)
          this.isCreatingReceipt = false
        })
      }
    },

    validateForm () {
      if (!this.cart[0]?.data?.name) {
        this.showMsgBox({ title: 'test', text: 'Potreban minimalno jedan artikl u košarici', actions: ['ok'], okBtnText: 'OK', color: 'error' })
        return
      }

      // Validacija eRačun emaila
      if (this.eracun && (!this.eracunEmail || this.eracunEmail.trim() === '')) {
        this.showMsgBox({ title: 'Greška', text: 'Email adresa je obavezna za kreiranje eRačuna', actions: ['ok'], okBtnText: 'OK', color: 'error' })
        return
      }

      if (this.paymentType.type !== 'BO') {
        this.rValidation()
      } else {
        this.isCreatingReceipt = true
        this.createReceipt().catch(err => {
          console.error('Error creating receipt:', err)
          this.isCreatingReceipt = false
        })
      }
    }
  },

  async mounted () {
    this.getMultiplier()
    this.addEmptyCartItem()

    this.getEuCountries()

    // NE pozivaj $off da ne ukloniš listenere drugih instanci!
    // EventBus.$off('set-selected-printer')
    EventBus.$on('set-selected-printer', (printer) => {
      this.selectedPrinter = printer
    })

    EventBus.$on('stop-loader', async () => {
      // Reagiraj samo ako je ovaj dialog inicirao kreiranje računa
      if (!this.isCreatingReceipt) {
        return
      }

      // Resetuj flag
      this.isCreatingReceipt = false

      if (this.tabMode) {
        // U tab mode-u, resetiraj loading i zatvori
        this.loading = false
        this.$emit('receipt-issued', this)
      } else {
        // U modal mode-u, resetiraj formu
        this.resetAll()
        this.loading = false
        // await this.getCashRegisters()
        await this.getCashRegister()
        this.onSelectPaymentType()
        this.calculateReferenceNumber()
      }
    })

    // NE pozivaj $off da ne ukloniš listenere drugih instanci!
    // EventBus.$off('error-return')
    EventBus.$on('error-return', () => {
      // PROVERI: reaguj samo ako je OVA instanca inicirala kreiranje računa
      if (!this.isCreatingReceipt) {
        return
      }

      this.loading = false
      this.isCreatingReceipt = false // Reset flag u slučaju greške
      // NE brišemo podatke o kupcu - korisnik može ispraviti grešku i ponovo poslati
    })

    // NE pozivaj $off da ne ukloniš listenere drugih instanci!
    // EventBus.$off('stop-loader-no-reset')
    EventBus.$on('stop-loader-no-reset', () => {
      // PROVERI: reaguj samo ako je OVA instanca inicirala kreiranje računa
      if (!this.isCreatingReceipt) {
        return
      }

      // Forsiraj postavljanje na false koristeći $set
      this.$set(this, 'loading', false)
      this.isCreatingReceipt = false

      // Forsiraj update
      this.$forceUpdate()
      // NE poziva this.resetAll() - artikli i podaci o kupcu ostaju za retry!
    })

    // NE pozivaj $off da ne ukloniš listenere drugih instanci!
    // EventBus.$off('showError')
    EventBus.$on('showError', (data) => {
      this.$nextTick(() => {
        if (this.$refs.showError && this.$refs.showError.open) {
          this.$refs.showError.open(data.message)
        } else {
          console.error('ShowError ref not available:', data.message)
          alert(data.message) // Fallback
        }
      })
    })

    this.selectedVies = 'inside'
    this.selectedCustomerType = 'legal'
    this.updateValueDate()
  },

  beforeDestroy () {
    // NE pozivaj $off da ne ukloniš listenere drugih instanci!
    // EventBus.$off('stop-loader-no-reset')
    if (this.registerSnapshotListener) {
      this.registerSnapshotListener()
      this.registerSnapshotListener = null
    }
    clearTimeout(this.oibSearchTimer)
    clearTimeout(this.nameSearchTimer)
    clearTimeout(this.searchInputTimer)
    this.detachListeners()
  }
}
</script>

<style scoped>
.totals-row {
  max-height: 3rem;
}

.total-price {
  display: block;
  font-size: 1.8rem;
  color: black;
  margin-top: 0.3rem;
  margin-right: 1rem;
}

.curreny-selector {
  max-width: 6rem;
}

.attention >>> .v-text-field__slot input {
  color: red;
}

.normal >>> .v-text-field__slot input {
  color: black;
}

.v-dialog__content >>> .my-custom-dialog {
  position: absolute;
  top: 30px;
  border-radius: 25px !important;
}

::v-deep .small-radio i {
  font-size: 15px;
}

::v-deep .delete-icon i {
  font-size: 6px;
}

::v-deep .small-radio label {
  font-size: 15px;
  padding-left: 0px;
  margin-left: -4px;
}

::v-deep .small-radio .v-radio {
  padding: 0px;
}

::v-deep .small-radio [class*="__ripple"] {
  left: 0;
}

.my-overlay >>> .v-overlay__content {
  width: 100%;
  height: 100%;
}

.datetimepicker {
  width: 100%;
  height: 40px !important;
}

.datepickerLabel {
  font-size: 12px !important;
  font-weight: bold;
}

/* Tab Mode styles */
.tab-mode-card {
  width: 100% !important;
  height: 100% !important;
  box-shadow: none !important;
  margin: 0 !important;
  border-radius: 0 !important;
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden !important;
}

.tab-mode-card .v-card__text {
  flex: 1 !important;
  overflow-y: auto !important;
  overflow-x: hidden !important;
}
</style>
